import { useCallback } from 'react'
import { config } from '../../../config'
import { UsePersistedSettings } from '../../player/use-persisted-channel-settings'
import {
  Params,
  getMixedNameFileToDownload
} from '../use-download-task-split/utils'
import { DownloadSongProps } from '../use-download-task-utils'
import { useTaskMaxDuration } from '../use-task-max-duration'
import { TrackAudio } from '../use-track-split'

export interface GenerateMixProps {
  format: string
  hasWavHighRes: boolean
  type: string | null
  bucket: string
  onDownloadSong(i: DownloadSongProps): void
}

export interface UseGenerateMix {
  onGenerateMix({
    format,
    hasWavHighRes,
    onDownloadSong
  }: GenerateMixProps): void
}

export interface MixEffects {
  tempo: number
  bpm: number | string
  key: string
  semitones: number
  originalTuning: any
  newTuning: number
}

export interface UseGenerateMixParams {
  trackId?: string
  name?: string
  tracks: TrackAudio[]
  isHifi?: boolean
  persistedSettings: UsePersistedSettings
}

const getSemitonesWithTuningOffset = (_mixEffects: any): any => {
  // getting semitones with tuning offset in the
  // semitone format (not cents) used by ffmpeg
  const semitones = _mixEffects?.newTuning
    ? (1200 *
        Math.log2(_mixEffects?.newTuning / _mixEffects?.originalTuning ?? 0) +
        _mixEffects?.semitones * 100) /
      100
    : _mixEffects?.semitones
  return semitones
}

export const useGenerateMixNew = ({
  trackId,
  name,
  tracks,
  isHifi,
  persistedSettings
}: UseGenerateMixParams): UseGenerateMix => {
  const maxDuration = useTaskMaxDuration({ isHifi })

  const onGenerateMix = useCallback(
    ({
      format,
      hasWavHighRes,
      type,
      bucket,
      onDownloadSong
    }: GenerateMixProps) => {
      const state = persistedSettings.onGetSettings()

      state?.channels?.map((i) => {
        const channel = i
        if (channel?.id === 'original_mix') {
          channel.id = 'other'
        }
        return channel
      })

      const mixEffects = {
        tempo: state?.general?.tempo || 1,
        bpm: state?.general?.bpm,
        key: state?.general?.key,
        semitones: state?.general?.semitones || 0,
        newTuning: state?.general?.newTuning || 440,
        originalTuning: state?.general?.originalTuning || 440
      }

      if (!format) throw new Error('file format is required')

      const dataTracks = tracks
        .filter((song) => {
          const stem = song?.name === 'accompaniment' ? 'other' : song?.name
          const track = state?.channels?.find((item) => item?.id === stem)

          return (
            track &&
            !track?.isMuted &&
            track?.realVolume &&
            track?.realVolume > 0
          )
        })
        .map((song) => {
          const stem = song?.name === 'accompaniment' ? 'other' : song?.name
          const track = state?.channels?.find((item) => item?.id === stem)
          const volume = track?.isMuted ? 0 : track?.volume || 0

          return {
            link: !hasWavHighRes
              ? undefined
              : song.bucket
                  .split('/')
                  .filter((value) => value !== '')
                  .join('/'),
            stem: song.name,
            volume,
            pan: `${track?.pan ? track?.pan : 0}`
          }
        })
        .reduce(
          (acc, current, index) =>
            Object.assign(acc, {
              [`stem[${index}]`]: current.stem,
              [`file[${index}]`]: current.link,
              [`volume[${index}]`]: current.volume,
              [`pan[${index}]`]: current.pan
            }),
          {
            tempo: mixEffects.tempo,
            pitch: 2 ** (getSemitonesWithTuningOffset(mixEffects) / 12)
          }
        )

      const songNameToQueued = name?.slice(0, 140) || 'mix'
      const songNameToDownload = getMixedNameFileToDownload({
        title: songNameToQueued,
        key: mixEffects.key,
        bpm: mixEffects.bpm,
        tuning: mixEffects.newTuning
      })

      const isCustomSeparate = type === 'custom-music-stems'
      const route = isCustomSeparate ? '/v2/mix' : '/mix'
      const path = !hasWavHighRes
        ? `${config.serviceHighRes.endpoint}${route}`
        : config.getAudioMix.endpoint

      const params: Params = {
        ...dataTracks,
        format,
        trackId: isCustomSeparate ? trackId : undefined,
        maxDuration: isCustomSeparate ? undefined : maxDuration,
        bitrate: format === 'mp3' ? '320' : undefined,
        type: hasWavHighRes || isCustomSeparate ? undefined : type,
        location:
          hasWavHighRes || isCustomSeparate ? undefined : bucket.substring(1)
      }

      // remove properties with undefined values
      Object.keys(params).forEach((key) => !params[key] && delete params[key])

      const query = `${new URLSearchParams(
        params
      ).toString()}&name=${encodeURIComponent(songNameToDownload)}`
      const link = `${path}/?${query}`

      onDownloadSong({
        url: link,
        title: songNameToQueued,
        filename: songNameToDownload,
        format: 'mix'
      })
    },
    [persistedSettings, tracks, name, trackId, maxDuration]
  )

  return {
    onGenerateMix
  }
}
